/**
 * VM Agent Contract Types
 *
 * Formal type definitions and Zod schemas for ALL request/response payloads
 * between the control plane (TypeScript API on Cloudflare Workers) and the
 * VM agent (Go on Hetzner VMs).
 *
 * These schemas serve as the single source of truth for the HTTP boundary
 * and can validate payloads at runtime on both sides.
 */
import { z } from 'zod';
// =============================================================================
// Shared Enums & Primitives
// =============================================================================
export const WorkspaceStatusSchema = z.enum([
    'creating',
    'running',
    'recovery',
    'stopped',
    'error',
]);
export const MessageRoleSchema = z.enum(['user', 'assistant', 'system', 'tool']);
// =============================================================================
// Standardized Error Response
// =============================================================================
export const ErrorResponseSchema = z.object({
    error: z.string(),
    message: z.string().optional(),
});
// =============================================================================
// Control Plane -> VM Agent: GET /health
// =============================================================================
// This endpoint is unauthenticated (used for monitoring/liveness checks),
// so it MUST NOT expose workspace IDs or other sensitive data.
export const HealthResponseSchema = z.object({
    status: z.literal('healthy'),
    // nodeId, activeWorkspaces, and sessions removed — the health endpoint
    // is unauthenticated, so it must not leak infrastructure identifiers
    // or operational metrics.
});
// =============================================================================
// Control Plane -> VM Agent: POST /workspaces
// =============================================================================
export const CreateWorkspaceAgentRequestSchema = z.object({
    workspaceId: z.string().min(1),
    repository: z.string(),
    branch: z.string(),
    callbackToken: z.string().optional(),
    gitUserName: z.string().nullish(),
    gitUserEmail: z.string().nullish(),
    githubId: z.string().nullish(),
    lightweight: z.boolean().optional(),
});
export const CreateWorkspaceAgentResponseSchema = z.object({
    workspaceId: z.string(),
    status: z.literal('creating'),
});
// =============================================================================
// Control Plane -> VM Agent: DELETE /workspaces/:id
// =============================================================================
export const DeleteWorkspaceAgentResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// Control Plane -> VM Agent: POST /workspaces/:id/agent-sessions
// =============================================================================
export const CreateAgentSessionAgentRequestSchema = z.object({
    sessionId: z.string().min(1),
    label: z.string().nullable(),
    chatSessionId: z.string().optional(),
    projectId: z.string().optional(),
    mcpServers: z.array(z.object({
        url: z.string().url(),
        token: z.string(),
    })).optional(),
});
export const AgentSessionResponseSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    status: z.string(),
    label: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    stoppedAt: z.string().nullable().optional(),
    suspendedAt: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    acpSessionId: z.string().optional(),
    agentType: z.string().optional(),
});
// =============================================================================
// VM Agent -> Control Plane (Callbacks):
// POST /api/workspaces/:id/ready
// =============================================================================
export const WorkspaceReadyRequestSchema = z.object({
    status: z.enum(['running', 'recovery']).optional(),
});
export const WorkspaceReadyResponseSchema = z.object({
    success: z.boolean(),
    reason: z.string().optional(),
});
// =============================================================================
// VM Agent -> Control Plane (Callbacks):
// POST /api/workspaces/:id/provisioning-failed
// =============================================================================
export const ProvisioningFailedRequestSchema = z.object({
    errorMessage: z.string().optional(),
});
export const ProvisioningFailedResponseSchema = z.object({
    success: z.boolean(),
    reason: z.string().optional(),
});
// =============================================================================
// VM Agent -> Control Plane (Callbacks):
// POST /api/workspaces/:id/messages
// =============================================================================
export const PersistMessageItemSchema = z.object({
    messageId: z.string().min(1),
    sessionId: z.string().min(1),
    role: MessageRoleSchema,
    content: z.string().min(1),
    toolMetadata: z.string().nullable().optional(),
    timestamp: z.string().min(1),
});
export const PersistMessageBatchRequestSchema = z.object({
    messages: z.array(PersistMessageItemSchema).min(1).max(100),
});
export const PersistMessageBatchResponseSchema = z.object({
    persisted: z.number().int().min(0),
    duplicates: z.number().int().min(0),
});
// =============================================================================
// JWT Token Claims
// =============================================================================
export const CallbackTokenClaimsSchema = z.object({
    workspace: z.string(),
    type: z.literal('callback'),
    scope: z.enum(['node', 'workspace']).optional(),
    iss: z.string(),
    sub: z.string(),
    aud: z.literal('workspace-callback'),
    exp: z.number(),
    iat: z.number(),
});
export const NodeManagementTokenClaimsSchema = z.object({
    type: z.literal('node-management'),
    node: z.string(),
    workspace: z.string().optional(),
    iss: z.string(),
    sub: z.string(),
    aud: z.literal('node-management'),
    exp: z.number(),
    iat: z.number(),
});
// =============================================================================
// ACP Session Reconciliation (VM Agent → Control Plane)
// Spec 027: DO-Owned ACP Session Lifecycle
// =============================================================================
export const AcpSessionStatusReportSchema = z.object({
    status: z.enum(['running', 'completed', 'failed']),
    acpSdkSessionId: z.string().optional(),
    errorMessage: z.string().optional(),
    nodeId: z.string().min(1),
});
export const AcpSessionHeartbeatSchema = z.object({
    nodeId: z.string().min(1),
    acpSdkSessionId: z.string().optional(),
});
export const AcpSessionReconciliationItemSchema = z.object({
    id: z.string(),
    chatSessionId: z.string(),
    workspaceId: z.string(),
    status: z.enum(['assigned', 'running']),
    initialPrompt: z.string().nullable(),
    agentType: z.string().nullable(),
});
export const AcpSessionReconciliationResponseSchema = z.object({
    sessions: z.array(AcpSessionReconciliationItemSchema),
});
// =============================================================================
// Contract Constants
// =============================================================================
/** Maximum messages per batch */
export const MAX_BATCH_SIZE = 100;
/** Default callback token expiry in milliseconds (24 hours) */
export const DEFAULT_CALLBACK_TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000;
/** Default node management token expiry in milliseconds (1 hour) */
export const DEFAULT_NODE_MANAGEMENT_TOKEN_EXPIRY_MS = 60 * 60 * 1000;
/** JWT algorithm used for all tokens */
export const JWT_ALGORITHM = 'RS256';
/** Audience values for token types */
export const JWT_AUDIENCES = {
    terminal: 'workspace-terminal',
    callback: 'workspace-callback',
    nodeManagement: 'node-management',
};
//# sourceMappingURL=vm-agent-contract.js.map